(() => {
  // Theme-editor section reloads may load this asset again.
  if (customElements.get('announcement-countdown')) return;

  function deadlineInZone(date, time, timeZone) {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
    if (!dateMatch || !timeMatch) return NaN;
    const [year, month, day] = dateMatch.slice(1).map(Number);
    const [hour, minute, second = 0] = timeMatch.slice(1).filter((part) => part !== undefined).map(Number);
    if (year < 1970 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59) return NaN;
    const wallTime = Date.UTC(year, month - 1, day, hour, minute, second);
    const calendar = new Date(wallTime);
    if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day) return NaN;

    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
      });
      let timestamp = wallTime;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const parts = Object.fromEntries(formatter.formatToParts(timestamp).map((part) => [part.type, part.value]));
        const representedTime = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
        const adjustment = wallTime - representedTime;
        if (adjustment === 0) return timestamp;
        timestamp += adjustment;
      }
    } catch (_) {
      // Invalid dates/time zones never show a misleading countdown.
    }
    return NaN;
  }

  function remainingTime(deadline, now) {
    const total = Math.max(0, Math.ceil((deadline - now) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return { total, hours, minutes, seconds, text: [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':') };
  }

  class AnnouncementCountdown extends HTMLElement {
    connectedCallback() {
      this.clock = this.querySelector('[data-countdown-clock]');
      this.notice = this.querySelector('[data-countdown-notice]');
      this.deadline = deadlineInZone(this.dataset.endDate.trim(), this.dataset.endTime.trim(), this.dataset.timeZone);
      if (!this.clock) return;
      this.updateCountdown();
      if (Number.isFinite(this.deadline) && this.deadline > Date.now()) {
        this.interval = window.setInterval(() => this.updateCountdown(), 1000);
        this.onVisibilityChange = () => this.updateCountdown();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    updateCountdown() {
      if (!Number.isFinite(this.deadline)) {
        this.finish('Choose a valid end date and time for this countdown.');
        return;
      }
      const remaining = remainingTime(this.deadline, Date.now());
      if (remaining.total === 0) {
        this.finish('This sale has ended. Choose a future end date to show a countdown.');
        return;
      }
      this.clock.textContent = remaining.text;
      this.clock.setAttribute('aria-label', `${remaining.hours} hours, ${remaining.minutes} minutes, ${remaining.seconds} seconds remaining`);
      this.hidden = false;
    }

    finish(editorMessage) {
      window.clearInterval(this.interval);
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.dataset.designMode === 'true') {
        this.clock.hidden = true;
        this.querySelector('[data-countdown-message]').hidden = true;
        this.notice.textContent = editorMessage;
        this.notice.hidden = false;
        this.hidden = false;
        return;
      }
      const utilityBar = this.closest('.utility-bar');
      const carousel = this.closest('slideshow-component');
      const slide = this.closest('.slideshow__slide');
      const region = this.closest('.announcement-bar');
      if (carousel && slide) {
        slide.remove();
        customElements.whenDefined('slideshow-component').then(() => {
          if (!carousel.isConnected) return;
          carousel.pause();
          carousel.sliderItems = carousel.querySelectorAll('[id^="Slide-"]');
          const remainingSlides = Array.from(carousel.sliderItems);
          carousel.sliderItemsToShow = remainingSlides;
          carousel.sliderFirstItemNode = remainingSlides[0];
          carousel.slider.scrollLeft = 0;
          carousel.currentPage = 1;
          carousel.hidden = remainingSlides.length === 0;
          [carousel.prevButton, carousel.nextButton].forEach((button) => { button.hidden = remainingSlides.length < 2; });
          if (remainingSlides.length > 1) {
            carousel.resetPages();
            if (carousel.slider.dataset.autoplay === 'true' && !carousel.reducedMotion.matches && !carousel.announcementBarArrowButtonWasClicked) carousel.play();
          } else {
            carousel.sliderItemOffset = remainingSlides[0]?.clientWidth || 1;
            carousel.totalPages = 1;
          }
          carousel.setSlideVisibility();
          this.hideEmptyBar(utilityBar);
        });
      } else if (region) {
        region.hidden = true;
        this.hideEmptyBar(utilityBar);
      } else {
        this.hidden = true;
      }
    }

    hideEmptyBar(utilityBar) {
      if (utilityBar && !utilityBar.querySelector('.announcement-bar:not([hidden]), .list-social a, localization-form')) utilityBar.hidden = true;
    }
  }

  customElements.define('announcement-countdown', AnnouncementCountdown);
})();
